import { useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import publicIp from 'public-ip';
import {
  useWriteUserActivityLogMutation,
  CapaActivityType,
  CapaServiceType
} from '@generated/graphql';
import useClient from './useClient';
import { STORAGE_ACTION, getStorageItem } from '@utils/storage';
import * as Amplitude from '@amplitude/node';
import AppConfig from '@config';
import {
  osName,
  osVersion,
  mobileVendor,
  deviceType,
  browserName,
  browserVersion
} from 'react-device-detect';
import jwtDecode from '@utils/jwtDecode';
import { useSnackbar } from '@components/Provider';

enum Enum_Action_Page_Enum {
  // ERror
  ERROR_MESSAGE = '에러 메시지',

  Page_Main = '페이지_메인',
  Page_Login = '페이지_로그인',

  /* 15차 추가로그*/
  Page_Quotation = '페이지_견적비교',
  Page_Quotation_ProjectDetail_Quotations = '페이지_견적비교_프로젝트상세_받은견적',
  Page_Quotation_ProjectDetail_Request = '페이지_견적비교_프로젝트상세_요청내역',
  Page_Chat = '페이지_채팅',
  Page_ChatRoom = '페이지_채팅방',

  Page_RFQ_ServiceType = '페이지_RFQ_전문서비스',
  Page_RFQ_CNC_ProcessMethod = '페이지_RFQ_CNC_가공방식',
  Page_RFQ_CNC_InputForm = '페이지_RFQ_CNC_정보입력',
  Page_RFQ_PRINTING_ProcessMethod = '페이지_RFQ_3D프린트_가공방식',
  Page_RFQ_PRINTING_InputForm = '페이지_RFQ_3D프린트_정보입력',
  Page_RFQ_INJ_ProcessMethod = '페이지_RFQ_금형사출_가공방식',
  Page_RFQ_INJ_InputForm = '페이지_RFQ_금형사출_정보입력',
  Page_RFQ_SHEET_METAL_ProcessMethod = '페이지_RFQ_판금가공_가공방식',
  Page_RFQ_SHEET_METAL_InputForm = '페이지_RFQ_판금가공_정보입력',
  Page_RFQ_DESIGN_ProcessMethod = '페이지_RFQ_기구설계_가공방식',
  Page_RFQ_DESIGN_InputForm = '페이지_RFQ_기구설계_정보입력',

  /* 16차 추가로그 */
  Page_MyPage_Review = '페이지_후기관리',
  Page_MyPage_Review_Available = '페이지_후기관리_작성가능한후기탭',
  Page_MyPage_Review_Wrote = '페이지_후기관리_내가작성한후기탭',

  /* 17차 추가로그 */
  Page_Quotation_ProjectDetail_Security = '페이지_견적비교_프로젝트상세_보안설정',
  Page_RFQ_CASTING_ProcessMethod = '페이지_RFQ_주조_가공방식',
  Page_RFQ_CASTING_InputForm = '페이지_RFQ_주조_정보입력',

  /* 20차 추가로그 */
  Page_MyPage_ITP = '페이지_ITP이벤트페이지',

  /* 21차 추가로그 */
  Page_RFQ_PRODUCT_DESIGN_InputForm = '페이지_RFQ_제품디자인_정보입력',

  /* JSB 추가로그 */
  Page_MyPage_JSB = '페이지_JSB이벤트페이지',

  /* 견적서 재삭성 추가로그 */
  Page_RFQ_Rewrite_CNC_ProcessMethod = '페이지_RFQ_재작성_CNC_2단계',
  Page_RFQ_Rewrite_CNC_InputForm = '페이지_RFQ_재작성_CNC_3단계',
  Page_RFQ_Rewrite_PRINTING_ProcessMethod = '페이지_RFQ_재작성_3D프린트_2단계',
  Page_RFQ_Rewrite_PRINTING_InputForm = '페이지_RFQ_재작성_3D프린트_3단계',
  Page_RFQ_Rewrite_INJ_ProcessMethod = '페이지_RFQ_재작성_금형사출_2단계',
  Page_RFQ_Rewrite_INJ_InputForm = '페이지_RFQ_재작성_금형사출_3단계',
  Page_RFQ_Rewrite_SHEET_METAL_ProcessMethod = '페이지_RFQ_재작성_판금가공_2단계',
  Page_RFQ_Rewrite_SHEET_METAL_InputForm = '페이지_RFQ_재작성_판금가공_3단계',
  Page_RFQ_Rewrite_DESIGN_ProcessMethod = '페이지_RFQ_재작성_디자인_2단계',
  Page_RFQ_Rewrite_DESIGN_InputForm = '페이지_RFQ_재작성_디자인_3단계',
  Page_RFQ_Rewrite_CASTING_ProcessMethod = '페이지_RFQ_재작성_주조_2단계',
  Page_RFQ_Rewrite_CASTING_InputForm = '페이지_RFQ_재작성_주조_3단계',

  /* 
    CAPA 3.0 
  */

  /* 캐파 뷰어 튜토리얼 페이지 */
  Page_Capa_Viewer_Visited = '캐파_뷰어_튜토리얼'
}

enum Enum_Action_Button_Enum {
  Button_Gnb_Login = '버튼_메인_GNB_고객로그인/회원가입',
  Button_Gnb_SignUp = '버튼_메인_GNB_회원가입',
  Button_Gnb_Tab_Dashboard = '버튼_메인_Tab_견적비교',
  Button_Gnb_Tab_Chat = '버튼_메인_Tab_채팅',
  Button_Gnb_Popup_Profile = '버튼_PopupMenu_프로필관리',
  Button_Gnb_Popup_Account = '버튼_PopupMenu_계정관리',
  Button_Gnb_Popup_Review = '버튼_PopupMenu_후기관리',
  Button_Gnb_Popup_LogOut = '버튼_PopupMenu_로그아웃',
  Button_Gnb_Popup_Event = '버튼_PopupMenu_이벤트참여',
  Button_Gnb_Popup_Notification = '버튼_PopupMenu_알림설정',

  Button_Notification_Setting = '버튼_알림탭_알림설정',

  Button_Chat_PartnerConfirm = '버튼_채팅방_업체확정',
  Button_Chat_DoPartnerConfirm = '버튼_채팅방_업체확정하기',

  Button_Profile_LogOut = '버튼_내정보관리_로그아웃',
  // Button_AccountPopUp_ClientType_CORPORATE = '버튼_고객유형팝업_법인사업자',
  // Button_AccountPopUp_ClientType_SELF = '버튼_고객유형팝업_개인사업자',
  // Button_AccountPopUp_ClientType_NON = '버튼_고객유형팝업_사업자등록번호없는_개인및단체',

  /* 15차 추가로그*/
  Button_RFQ_Select_ServiceType = '버튼_RFQ_전문서비스선택',

  Button_RFQ_CNC_About_ProcessMethod = '버튼_RFQ_CNC_가공방식이궁금하다면?',
  Button_RFQ_CNC_Select_ProcessMethod = '버튼_RFQ_CNC_가공방식선택',
  Button_RFQ_CNC_Upload_DrwaingFile = '버튼_RFQ_CNC_도면업로드',
  Button_RFQ_CNC_Etc = '버튼_RFQ_CNC_상세요청사항',
  Button_RFQ_CNC_ProjectTitle = '버튼_RFQ_CNC_프로젝트명',
  Button_RFQ_CNC_DueDate = '버튼_RFQ_CNC_납기일',
  Button_RFQ_CNC_EstimatedBudget_MIN = '버튼_RFQ_CNC_추정예산_최소',
  Button_RFQ_CNC_EstimatedBudget_MAX = '버튼_RFQ_CNC_추정예산_최대',
  Button_RFQ_CNC_Preview = '버튼_RFQ_CNC_작성완료',
  Button_RFQ_CNC_Prev = '버튼_RFQ_CNC_이전단계',
  Button_RFQ_CNC_Submit = '버튼_RFQ_CNC_견적요청완료',
  Button_RFQ_CNC_Select_Options = '버튼_RFQ_CNC_재료옵션선택',
  Button_RFQ_CNC_Batch_Application_Options = '버튼_RFQ_CNC_도면_일괄적용',
  Button_RFQ_CNC_NO_DRAWING_FILE = '버튼_RFQ_CNC_도면이없다면?',
  Button_RFQ_CNC_ProjectPurpose = '버튼_RFQ_CNC_제품용도',

  Button_RFQ_PRINTING_About_ProcessMethod = '버튼_RFQ_3D프린팅_가공방식이궁금하다면?',
  Button_RFQ_PRINTING_Select_ProcessMethod = '버튼_RFQ_3D프린팅_가공방식선택',
  Button_RFQ_PRINTING_Upload_DrwaingFile = '버튼_RFQ_3D프린팅_도면업로드',
  Button_RFQ_PRINTING_Etc = '버튼_RFQ_3D프린팅_상세요청사항',
  Button_RFQ_PRINTING_ProjectTitle = '버튼_RFQ_3D프린팅_프로젝트명',
  Button_RFQ_PRINTING_DueDate = '버튼_RFQ_3D프린팅_납기일',
  Button_RFQ_PRINTING_EstimatedBudget_MIN = '버튼_RFQ_3D프린팅_추정예산_최소',
  Button_RFQ_PRINTING_EstimatedBudget_MAX = '버튼_RFQ_3D프린팅_추정예산_최대',
  Button_RFQ_PRINTING_Preview = '버튼_RFQ_3D프린팅_작성완료',
  Button_RFQ_PRINTING_Prev = '버튼_RFQ_3D프린팅_이전단계',
  Button_RFQ_PRINTING_Submit = '버튼_RFQ_3D프린팅_견적요청완료',
  Button_RFQ_PRINTING_Select_Options = '버튼_RFQ_3D프린팅_재료옵션선택',
  Button_RFQ_PRINTING_Batch_Application_Options = '버튼_RFQ_3D프린팅_도면_일괄적용',
  Button_RFQ_PRINTING_NO_DRAWING_FILE = '버튼_RFQ_3D프린팅_도면이없다면?',
  Button_RFQ_PRINTING_ProjectPurpose = '버튼_RFQ_3D프린팅_제품용도',

  Button_RFQ_INJ_About_ProcessMethod = '버튼_RFQ_금형사출_가공방식이궁금하다면?',
  Button_RFQ_INJ_Select_ProcessMethod = '버튼_RFQ_금형사출_가공방식선택',
  Button_RFQ_INJ_Upload_DrwaingFile = '버튼_RFQ_금형사출_도면업로드',
  Button_RFQ_INJ_Etc = '버튼_RFQ_금형사출_상세요청사항',
  Button_RFQ_INJ_ProjectTitle = '버튼_RFQ_금형사출_프로젝트명',
  Button_RFQ_INJ_DueDate = '버튼_RFQ_금형사출_납기일',
  Button_RFQ_INJ_EstimatedBudget_MIN = '버튼_RFQ_금형사출_추정예산_최소',
  Button_RFQ_INJ_EstimatedBudget_MAX = '버튼_RFQ_금형사출_추정예산_최대',
  Button_RFQ_INJ_Preview = '버튼_RFQ_금형사출_작성완료',
  Button_RFQ_INJ_Prev = '버튼_RFQ_금형사출_이전단계',
  Button_RFQ_INJ_Submit = '버튼_RFQ_금형사출_견적요청완료',
  Button_RFQ_INJ_Select_Options = '버튼_RFQ_금형사출_재료옵션선택',
  Button_RFQ_INJ_Batch_Application_Options = '버튼_RFQ_금형사출_도면_일괄적용',
  Button_RFQ_INJ_NO_DRAWING_FILE = '버튼_RFQ_금형사출_도면이없다면?',
  Button_RFQ_INJ_ProjectPurpose = '버튼_RFQ_금형사출_제품용도',

  Button_RFQ_SHEET_METAL_About_ProcessMethod = '버튼_RFQ_판금가공_가공방식이궁금하다면?',
  Button_RFQ_SHEET_METAL_Select_ProcessMethod = '버튼_RFQ_판금가공_가공방식선택',
  Button_RFQ_SHEET_METAL_Upload_DrwaingFile = '버튼_RFQ_판금가공_도면업로드',
  Button_RFQ_SHEET_METAL_Etc = '버튼_RFQ_판금가공_상세요청사항',
  Button_RFQ_SHEET_METAL_ProjectTitle = '버튼_RFQ_판금가공_프로젝트명',
  Button_RFQ_SHEET_METAL_DueDate = '버튼_RFQ_판금가공_납기일',
  Button_RFQ_SHEET_METAL_EstimatedBudget_MIN = '버튼_RFQ_판금가공_추정예산_최소',
  Button_RFQ_SHEET_METAL_EstimatedBudget_MAX = '버튼_RFQ_판금가공_추정예산_최대',
  Button_RFQ_SHEET_METAL_Preview = '버튼_RFQ_판금가공_작성완료',
  Button_RFQ_SHEET_METAL_Prev = '버튼_RFQ_판금가공_이전단계',
  Button_RFQ_SHEET_METAL_Submit = '버튼_RFQ_판금가공_견적요청완료',
  Button_RFQ_SHEET_METAL_Select_Options = '버튼_RFQ_판금가공_재료옵션선택',
  Button_RFQ_SHEET_METAL_Batch_Application_Options = '버튼_RFQ_판금가공_도면_일괄적용',
  Button_RFQ_SHEET_METAL_NO_DRAWING_FILE = '버튼_RFQ_판금가공_도면이없다면?',
  Button_RFQ_SHEET_METAL_ProjectPurpose = '버튼_RFQ_판금가공_제품용도',

  Button_RFQ_DESIGN_About_ProcessMethod = '버튼_RFQ_디자인/설계_가공방식이궁금하다면?',
  Button_RFQ_DESIGN_Etc = '버튼_RFQ_디자인/설계_상세요청사항',
  Button_RFQ_DESIGN_ProjectTitle = '버튼_RFQ_디자인/설계_프로젝트명',
  Button_RFQ_DESIGN_DueDate = '버튼_RFQ_디자인/설계_납기일',
  Button_RFQ_DESIGN_EstimatedBudget = '버튼_RFQ_디자인/설계_추정예산',
  Button_RFQ_DESIGN_Preview = '버튼_RFQ_디자인/설계_작성완료',
  Button_RFQ_DESIGN_Submit = '버튼_RFQ_디자인/설계_견적요청완료',
  Button_RFQ_DESIGN_ProjectPurpose = '버튼_RFQ_디자인/설계_제품용도',
  Button_RFQ_DESIGN_EstimatedBudget_MIN = '버튼_RFQ_디자인/설계_추정예산_최소',
  Button_RFQ_DESIGN_EstimatedBudget_MAX = '버튼_RFQ_디자인/설계_추정예산_최대',
  Button_RFQ_DESIGN_Prev = '버튼_RFQ_주조_이전단계',

  Button_RFQ_CASTING_About_ProcessMethod = '버튼_RFQ_주조_가공방식이궁금하다면?',
  Button_RFQ_CASTING_Select_ProcessMethod = '버튼_RFQ_주조_가공방식선택',
  Button_RFQ_CASTING_Upload_DrwaingFile = '버튼_RFQ_주조_도면업로드',
  Button_RFQ_CASTING_Etc = '버튼_RFQ_주조_상세요청사항',
  Button_RFQ_CASTING_ProjectTitle = '버튼_RFQ_주조_프로젝트명',
  Button_RFQ_CASTING_DueDate = '버튼_RFQ_주조_납기일',
  Button_RFQ_CASTING_EstimatedBudget_MIN = '버튼_RFQ_주조_추정예산_최소',
  Button_RFQ_CASTING_EstimatedBudget_MAX = '버튼_RFQ_주조_추정예산_최대',
  Button_RFQ_CASTING_Preview = '버튼_RFQ_주조_작성완료',
  Button_RFQ_CASTING_Prev = '버튼_RFQ_주조_이전단계',
  Button_RFQ_CASTING_Submit = '버튼_RFQ_주조_견적요청완료',
  Button_RFQ_CASTING_Select_Options = '버튼_RFQ_주조_재료옵션선택',
  Button_RFQ_CASTING_Batch_Application_Options = '버튼_RFQ_주조_도면_일괄적용',
  Button_RFQ_CASTING_NO_DRAWING_FILE = '버튼_RFQ_주조_도면이없다면?',
  Button_RFQ_CASTING_ProjectPurpose = '버튼_RFQ_주조_제품용도',

  Button_RFQ_PCB_About_ProcessMethod = '버튼_RFQ_PCB_가공방식이궁금하다면?',
  Button_RFQ_PCB_Select_ProcessMethod = '버튼_RFQ_PCB_가공방식선택',
  Button_RFQ_PCB_Upload_DrwaingFile = '버튼_RFQ_PCB_도면업로드',
  Button_RFQ_PCB_Etc = '버튼_RFQ_PCB_상세요청사항',
  Button_RFQ_PCB_ProjectTitle = '버튼_RFQ_PCB_프로젝트명',
  Button_RFQ_PCB_DueDate = '버튼_RFQ_PCB_납기일',
  Button_RFQ_PCB_EstimatedBudget_MIN = '버튼_RFQ_PCB_추정예산_최소',
  Button_RFQ_PCB_EstimatedBudget_MAX = '버튼_RFQ_PCB_추정예산_최대',
  Button_RFQ_PCB_Preview = '버튼_RFQ_PCB_작성완료',
  Button_RFQ_PCB_Prev = '버튼_RFQ_PCB_이전단계',
  Button_RFQ_PCB_Submit = '버튼_RFQ_PCB_견적요청완료',
  Button_RFQ_PCB_Select_Options = '버튼_RFQ_PCB_재료옵션선택',
  Button_RFQ_PCB_Batch_Application_Options = '버튼_RFQ_PCB_도면_일괄적용',
  Button_RFQ_PCB_NO_DRAWING_FILE = '버튼_RFQ_PCB_도면이없다면?',
  Button_RFQ_PCB_ProjectPurpose = '버튼_RFQ_PCB_제품용도',

  Button_SignUp_Email = '버튼_회원가입_이메일',
  Button_SignUp_Password = '버튼_회원가입_비밀번호',
  Button_SignUp_Agree_ServiceAndTerms = '버튼_회원가입_필수동의사항',
  Button_SignUp_Agree_Marketing = '버튼_회원가입_마케팅동의',
  Button_SignUp_Submit = '버튼_회원가입_회원가입완료',

  Button_Quotation_EventBanner_Invite = '버튼_견적비교_이벤트배너_친구초대',
  Button_Quotation_EventBanner_Confirm = '버튼_견적비교_이벤트배너_업체확정',
  Button_Quotation_EventBanner_Complete = '버튼_견적비교_이벤트배너_거래완료',
  Button_Quotation_ProjectDetail = '버튼_견적비교_프로젝트상세',
  Button_Quotation_ProjectDetail_ExpiredByClient = '버튼_견적비교_프로젝트상세_요청마감',
  Button_Quotation_ProjectDetail_Quotations = '버튼_견적비교_프로젝트상세_받은견적',
  Button_Quotation_ProjectDetail_Request = '버튼_견적비교_프로젝트상세_요청내역',
  Button_Quotation_ProjectDetail_Request_Download_DrawingFileAll = '버튼_견적비교_프로젝트상세_요청내역_전체도면다운로드',
  Button_Quotation_ProjectDetail_Request_Download_ReferenceAll = '버튼_견적비교_프로젝트상세_전체첨부파일다운로드',
  Button_Quotation_ProjectDetail_Confirm = '버튼_견적비교_프로젝트상세_업체확정',
  Button_Quotation_ProjectDetail_Complete = '버튼_견적비교_프로젝트상세_거래완료',
  Button_Quotation_ProjectDetail_Request_ExpiredByClient = '버튼_견적비교_프로젝트상세_요청내역_요청마감',
  Button_Quotation_ProjectDetail_Request_DrwaingFile_View = '버튼_견적비교_프로젝트상세_요청내역_도면보기',
  Button_Quotation_ProjectDetail_SelectPartner = '버튼_견적비교_프로젝트상세_파트너선택',

  Button_Request_Detail_FileDownload = '버튼_견적요청_상세_파일다운로드',
  Button_Confirm_Popup_Close = '버튼_업체확정팝업_닫기',
  Button_Confirm_Popup_Confirm = '버튼_업체확정팝업_업체확정',
  Button_Complete_Popup_Close = '버튼_거래완료팝업_닫기',
  Button_Complete_Popup_Complete = '버튼_거래완료팝업_거래완료',

  Button_ChatRoom_PartnerInfo = '버튼_채팅방_파트너정보',
  Button_ChatRoom_MessageButton_DetailedQuotation = '버튼_채팅방_상세견적서',
  Button_ChatRoom_Viewer = '버튼_채팅방_도면뷰어',
  Button_ChatRoom_MyRequest = '버튼_채팅방_내요청내역',
  Button_ChatRoom_MyRequest_DrwaingFile = '버튼_채팅방_내요청내역_도면클릭',
  Button_ChatRoom_MyRequest_Download_DrwaingFileAll = '버튼_채팅방_내요청내역_전체도면다운로드',
  Button_ChatRoom_MyRequest_Download_ReferenceAll = '버튼_채팅방_내요청내역_전체첨부파일다운로드',
  Button_ChatRoom_DetailedQuotation = '버튼_채팅방_상세견적서탭',
  Button_ChatRoom_DetailedQuotation_Download = '버튼_채팅방_상세견적서탭_다운로드',
  Button_ChatRoom_DetailedQuotationFile = '버튼_채팅방_상세견적서파일',
  Button_ChatRoom_Confirm = '버튼_채팅방_업체확정',
  Button_ChatRoom_Confirm_Popup_Close = '버튼_채팅방_업체확정_팝업닫기',
  Button_ChatRoom_Confirm_Popup_Confirm = '버튼_채팅방_업체확정_팝업업체확정',
  Button_ChatRoom_Complete = '버튼_채팅방_거래완료',
  Button_ChatRoom_Complete_Popup_Close = '버튼_채팅방_거래완료_팝업닫기',
  Button_ChatRoom_Complete_Popup_Complete = '버튼_채팅방_거래완료_팝업거래완료',

  Button_Chat = '버튼_채팅',
  Button_Chat_ChatRoom = '버튼_채팅_채팅방',
  Button_Chat_Search = '버튼_채팅_검색',

  Button_Gnb_CreateRfq = '버튼_GNB_견적요청하기',

  Button_Main_About_ServiceTypes = '버튼_랜딩_메인_가공방식이더궁금하다면?',
  Button_Main_Process_STEP1 = '버튼_랜딩_프로세스_Step1',
  Button_Main_Process_STEP2 = '버튼_랜딩_프로세스_Step2',
  Button_Main_Process_STEP3 = '버튼_랜딩_프로세스_Step3',
  Button_Main_Process_STEP4 = '버튼_랜딩_프로세스_Step4',
  Button_Main_Subscribtion = '버튼_랜딩_구독하기',

  Button_Main_News_About_Molding = '버튼_랜딩_제조소식_사출금형총정리',
  Button_Main_News_About_CNC = '버튼_랜딩_제조소식_CNC총정리',
  Button_Main_News_About_Printing = '버튼_랜딩_제조소식_3DP총정리',
  Button_Main_GettingStart = '버튼_랜딩_지금시작하기',

  Button_Main_Join_Partner_Next = '버튼_랜딩_파트너대모집_다음',
  Button_Main_Join_Partner_Prev = '버튼_랜딩_파트너대모집_이전',

  Button_Main_Footer_Blog = '버튼_랜딩_CAPA블로그',
  Button_Main_Footer_Event = '버튼_랜딩_이벤트',
  Button_Main_Footer_TermsAndService = '버튼_랜딩_CAPA이용약관',
  Button_Main_Footer_Private = '버튼_랜딩_개인정보처리방침',
  Button_Main_Footer_ChannelTalk = '버튼_랜딩_채널톡',

  /* 16차 추가 로그 */
  Button_MyPage_Reveiw_Available = '버튼_마이페이지_후기관리_작성가능한후기탭',
  Button_MyPage_Reveiw_Available_Write = '버튼_마이페이지_후기관리_작성가능한후기탭_후기작성하기',
  Button_MyPage_Reveiw_Wrote = '버튼_마이페이지_후기관리_내가작성한후기탭',
  Button_MyPage_Reveiw_Wrote_Modify = '버튼_마이페이지_후기관리_내가작성한후기탭_수정하기',

  Button_Quotation_ProjectDetail_Review = '버튼_받은견적_프로젝트상세_후기작성',
  Button_Chatroom_Review = '버튼_채팅방_후기작성',
  Button_Chatroom_CompleteBubble_Review = '버튼_채팅방_거래완료버블_후기작성',
  Button_Complete_Popup_Review = '버튼_거래완료팝업_후기작성',

  Button_SignUp_SendAuthEmail = '버튼_회원가입_인증이메일발송',
  Button_SignUp_VerifyingCode = '버튼_회원가입_인증하기',
  Button_Banner_RequiredInfo = '버튼_필수정보입력배너',

  /* 17차 추가로그 */
  Button_Quotation_ProjectDetail_SecuritySetting = '버튼_견적비교_프로젝트상세_보안설정',
  Button_RFQ_Survey = '버튼_RFQ_원하는제조서비스가없나요?',
  Button_RFQ_Survey_Submit = '버튼_RFQ_원하는제조서비스가없나요?_제출',
  Button_RFQ_NoDrwaing = '버튼_RFQ_도면이없다면?',
  Button_RFQ_NoDrwaing_UseMD = '버튼_RFQ_도면이없다면?_기구설계서비스이용하기',
  Button_RFQ_NoDrwaing_AboutMD = '버튼_RFQ_도면이없다면?_기구설계더알아보기',
  Button_RFQ_AllowFileDownload = '버튼_RFQ_모든파트너도면다운로드',
  Button_RFQ_LimitFileDownload = '버튼_RFQ_허용된파트너만도면다운로드가능',
  Button_Quotation_ProjectDetail_SecuritySetting_Toggle_Allow = '버튼_보안설정탭_허용토글',
  Button_Quotation_ProjectDetail_SecuritySetting_Toggle_Deny = '버튼_보안설정탭_제한토글',
  Button_ChatRoom_Toggle_Allow = '버튼_채팅방_허용토글',
  Button_ChatRoom_Toggle_Deny = '버튼_채팅방_제한토글',
  Button_PermissionPopup_Allow = '버튼_권한설정팝업_허용하기',
  Button_PermissionPopup_Deny = '버튼_권한설정팝업_제한하기',
  Button_ChatRoom_AllowRequestBubble_Cta = '버튼_채팅방_권한요청버블',
  Button_ChatRoom_EvaluatePartner = '버튼_채팅방_상담파트너평가',
  Button_ChatRoom_EvaluatePartner_Submit = '버튼_채팅방_상담파트너평가_제출',

  /* 18차 추가 로그 */
  Button_GNB_Banner_ProfileUpdateDialog = '버튼_GNB하단배너_필수정보입력',
  Button_ProfileUpdateDialog_Submit = '버튼_필수정보입력_제출하기',

  Button_Request_ProfileUpdateDialog_Submit = '버튼_RFQ_필수정보입력_제출하기',
  Button_Request_ProfileUpdateDialog_Close = '버튼_RFQ_필수정보입력_닫기',
  Button_Request_NotLoggedin_Login = '버튼_RFQ_비회원창_로그인',
  Button_Request_NotLoggedin_Registration = '버튼_RFQ_비회원창_회원가입',
  Button_Gnb_Popup_SwitchService = '버튼_PopupMenu_파트너계정전환',
  Button_Mypage_SwitchService = '버튼_내정보관리_파트너계정전환',
  Button_Mypage_Update_IsBiz = '버튼_내정보관리_사업자여부',
  Button_Mypage_Update_IsBiz_Submit = '버튼_내정보관리_사업자여부_변경하기',
  Button_Mypage_Update_DepartmentAndPosition = '버튼_내정보관리_부서및직함',
  Button_Mypage_Update_DepartmentAndPosition_Submit = '버튼_내정보관리_부서및직함_저장하기',

  /* 19차 추가 로그 */
  Button_GNB_Checkerboard = '버튼_GNB_바둑판',
  Button_GNB_Checkerboard_Capa = '버튼_GNB_바둑판_캐파',
  Button_GNB_Checkerboard_Partners = '버튼_GNB_바둑판_캐파파트너스',
  Button_GNB_Checkerboard_Connect = '버튼_GNB_바둑판_캐파커넥트',
  Button_GNB_Cloud = '버튼_GNB_클라우드',
  Button_GNB_Cloud_Detail = '버튼_GNB_클라우드_자세히보기',
  Button_RFQ_GetCloudDrwaing = '버튼_RFQ_클라우드에서가져오기',

  /* 20차 추가 로그 */
  Button_Main_ITP_Popup_Request = '버튼_랜딩페이지_팝업_제조하러가기',
  Button_Main_ITP_Popup_Detail = '버튼_랜딩페이지_팝업_자세히보기',
  Button_Main_ITP_Popup_7days = '버튼_랜딩페이지_팝업_7일간보지않기',
  Button_Main_ITP_Popup_Close = '버튼_랜딩페이지_팝업_닫기',

  Button_ITP_FloatingButton = '버튼_ITP아이콘',

  Button_RFQ_ITP_Popup_Confirm = '버튼_RFQ1단계_ITP주의사항_이벤트참여하기',
  Button_RFQ_ITP_Popup_NoITP = '버튼_RFQ1단계_ITP주의사항_일반RFQ작성페이지로이동',

  Button_Mypage_ITP_RewardDetail = '버튼_ITP이벤트페이지_제품상세정보보기',
  Button_Mypage_ITP_Request = '버튼_ITP이벤트페이지_ITP견적요청서작성하기',
  Button_Mypage_ITP_MyRequests = '버튼_ITP이벤트페이지_요청현황보러가기',
  Button_Mypage_ITP_RewardRequest = '버튼_ITP이벤트페이지_보상신청하기',

  Button_Quotation_ProjectDetail_ITPDetail = '버튼_요청현황_프로젝트상세_ITP이벤트자세히보기',
  Button_Quotation_ProjectDetail_ITPRewardRequest = '버튼_요청현황_프로젝트상세_ITP이벤트보상신청',

  Button_Quotation_ProjectDetail_JSBDetail = '버튼_요청현황_프로젝트상세_JSB이벤트자세히보기',
  Button_Quotation_ProjectDetail_JSBRewardRequest = '버튼_요청현황_프로젝트상세_JSB이벤트보상신청',

  Button_ChatRoom_ITPDetail = '버튼_채팅방_이벤트자세히보기',
  Button_ChatRoom_ITPRewardRequest = '버튼_채팅방_이벤트보상신청',
  Button_ChatRoom_ITPBubble_RewardReqest = '버튼_채팅방_채팅버블_이벤트보상신청하러가기CTA',

  /* 21차 추가 로그 */
  Button_Landing_ServiceTypeCard_LeftArrow = '버튼_랜딩페이지_전문서비스카드_좌',
  Button_Landing_ServiceTypeCard_RightArrow = '버튼_랜딩페이지_전문서비스카드_우',
  Button_Landing_ServiceTypeCard_CNC_Milling = '버튼_랜딩페이지_전문서비스카드_CNC_밀링',
  Button_Landing_ServiceTypeCard_CNC_Turning = '버튼_랜딩페이지_전문서비스카드_CNC_선반',
  Button_Landing_ServiceTypeCard_CNC_MachiningCenter = '버튼_랜딩페이지_전문서비스카드_CNC_머시닝센터',
  Button_Landing_ServiceTypeCard_CNC_Drilling = '버튼_랜딩페이지_전문서비스카드_CNC_드릴링',
  Button_Landing_ServiceTypeCard_CNC_Ref = '버튼_랜딩페이지_전문서비스카드_CNC_파트너추천방식으로견적요청하기',
  Button_Landing_ServiceTypeCard_PRINTING_SLA = '버튼_랜딩페이지_전문서비스카드_3DP_SLA',
  Button_Landing_ServiceTypeCard_PRINTING_FDM = '버튼_랜딩페이지_전문서비스카드_3DP_FDM',
  Button_Landing_ServiceTypeCard_PRINTING_Polyjet = '버튼_랜딩페이지_전문서비스카드_3DP_Polyjet',
  Button_Landing_ServiceTypeCard_PRINTING_MJF = '버튼_랜딩페이지_전문서비스카드_3DP_MJF',
  Button_Landing_ServiceTypeCard_PRINTING_SLS = '버튼_랜딩페이지_전문서비스카드_3DP_SLS',
  Button_Landing_ServiceTypeCard_PRINTING_SLMDMLS = '버튼_랜딩페이지_전문서비스카드_3DP_SLM_DMLS',
  Button_Landing_ServiceTypeCard_PRINTING_Ref = '버튼_랜딩페이지_전문서비스카드_3DP_파트너추천방식으로견적요청하기',
  Button_Landing_ServiceTypeCard_INJ_Injection = '버튼_랜딩페이지_전문서비스카드_금형사출_일반사출',
  Button_Landing_ServiceTypeCard_INJ_DoubleShotInjection = '버튼_랜딩페이지_전문서비스카드_금형사출_이중사출',
  Button_Landing_ServiceTypeCard_INJ_Blow = '버튼_랜딩페이지_전문서비스카드_금형사출_블로우사출',
  Button_Landing_ServiceTypeCard_INJ_Insert = '버튼_랜딩페이지_전문서비스카드_금형사출_인서트사출',
  Button_Landing_ServiceTypeCard_INJ_Ref = '버튼_랜딩페이지_전문서비스카드_금형사출_파트너추천방식으로견적요청하기',
  Button_Landing_ServiceTypeCard_SHEET_METAL_Press = '버튼_랜딩페이지_전문서비스카드_판금가공_프레스',
  Button_Landing_ServiceTypeCard_SHEET_METAL_Laser = '버튼_랜딩페이지_전문서비스카드_판금가공_레이저커팅',
  Button_Landing_ServiceTypeCard_SHEET_METAL_Waterjet = '버튼_랜딩페이지_전문서비스카드_판금가공_워터젯커팅',
  Button_Landing_ServiceTypeCard_SHEET_METAL_Ref = '버튼_랜딩페이지_전문서비스카드_판금가공_파트너추천방식으로견적요청하기',
  Button_Landing_ServiceTypeCard_CASTING_Sand = '버튼_랜딩페이지_전문서비스카드_주조_사형주조',
  Button_Landing_ServiceTypeCard_CASTING_Shell = '버튼_랜딩페이지_전문서비스카드_주조_쉘주조',
  Button_Landing_ServiceTypeCard_CASTING_Investment = '버튼_랜딩페이지_전문서비스카드_주조_인베스트먼트주조',
  Button_Landing_ServiceTypeCard_CASTING_Die = '버튼_랜딩페이지_전문서비스카드_주조_다이캐스팅',
  Button_Landing_ServiceTypeCard_CASTING_Centrifugal = '버튼_랜딩페이지_전문서비스카드_주조_원심주조',
  Button_Landing_ServiceTypeCard_CASTING_Ref = '버튼_랜딩페이지_전문서비스카드_주조_파트너추천방식으로견적요청하기',
  Button_Landing_ServiceTypeCard_CNC = '버튼_랜딩페이지_전문서비스카드_CNC',
  Button_Landing_ServiceTypeCard_PRINTING = '버튼_랜딩페이지_전문서비스카드_3DP',
  Button_Landing_ServiceTypeCard_INJ = '버튼_랜딩페이지_전문서비스카드_금형사출',
  Button_Landing_ServiceTypeCard_SHEET_METAL = '버튼_랜딩페이지_전문서비스카드_판금가공',
  Button_Landing_ServiceTypeCard_CASTING = '버튼_랜딩페이지_전문서비스카드_주조',
  Button_Landing_ServiceTypeCard_PRODUCT_DESIGN = '버튼_랜딩페이지_전문서비스카드_제품디자인',
  Button_Landing_ServiceTypeCard_DESIGN = '버튼_랜딩페이지_전문서비스카드_기구설계',
  Button_Landing_ServiceTypeCard_DESIGN_Request = '버튼_랜딩페이지_전문서비스카드_기구설계_기구설계견적요청서작성하기',
  Button_Landing_ServiceTypeCard_PRODUCT_DESIGN_Request = '버튼_랜딩페이지_전문서비스카드_제품디자인_제품디자인견적요청서작성하기',
  Button_Landing_Process_Request = '버튼_랜딩페이지_프로세스_견적요청하기',
  Button_Landing_Process_Compare = '버튼_랜딩페이지_프로세스_견적비교하기',
  Button_Landing_Process_Consulting = '버튼_랜딩페이지_프로세스_직접상담하기',
  Button_Landing_MoreKnowledge = '버튼_랜딩페이지_제조지식이더궁금하다면?',
  Button_Landing_BlogCard = '버튼_랜딩페이지_블로그카드',
  Button_Landing_Subscription_input = '버튼_랜딩페이지_구독이메일입력필드',
  Button_Landing_Subscription_button = '버튼_랜딩페이지_구독하기',
  Button_Landing_StartNow = '버튼_랜딩페이지_지금시작하기',
  Button_Landing_JoinPartner = '버튼_랜딩페이지_파트너가입하기',
  Button_Landing_AboutPartner_LeftArrow = '버튼_랜딩페이지_파트너설명_좌',
  Button_Landing_AboutPartner_RightArrow = '버튼_랜딩페이지_파트너설명_우',

  Button_RFQ_DESIGN_Upload_RepresentImage = '버튼_RFQ_기구설계_대표이미지업로드',
  Button_RFQ_DESIGN_Upload_ReferenceFile = '버튼_RFQ_기구설계_참고파일업로드',
  Button_Login_ShowPassword = '버튼_로그인_비밀번호표시',
  Button_Mypage_Update_ResetPassword_ShowPassword = '버튼_내정보관리_비밀번호재설정_비밀번호표시',
  Button_Signup_Password_ShowPassword = '버튼_회원가입_비밀번호표시',
  Button_ProfileUpdateDialog_Skip = '버튼_필수정보입력팝업_견적요청완료후입력',
  Button_GNB_Event = '버튼_GNB_이벤트',

  //졸싸빠 로그
  Button_Mypage_JSB_Request = '버튼_JSB이벤트페이지_견적요청서작성하기',
  Button_Mypage_JSB_MyRequests = '버튼_JSB이벤트페이지_요청현황보러가기',
  Button_Mypage_JSB_RewardRequest = '버튼_JSB이벤트페이지_보상신청하기',

  /* 삭제 및 변경 대상 시작*/
  // Button_Main_GNB_Request = '버튼_메인_GNB_견적요청하기',
  // Button_Request_step1_CNC = '버튼_견적요청_step1_CNC',
  // Button_Request_step1_Printing = '버튼_견적요청_step1_3D프린트',
  // Button_Request_step1_Molding = '버튼_견적요청_step1_금형사출',
  // Button_Request_step1_SheetMetal = '버튼_견적요청_step1_판금가공',
  // Button_Request_step1_MD = '버튼_견적요청_step1_기구설계',
  // Button_Request_step1_Casting = '버튼_견적요청_step1_주조',
  // Button_Request_step3_CNC_Submit = '버튼_견적요청_step3_CNC_견적요청완료하기',
  // Button_Request_step3_Printing_Submit = '버튼_견적요청_step3_3D프린트_견적요청완료하기',
  // Button_Request_step3_Molding_Submit = '버튼_견적요청_step3_금형사출_견적요청완료하기',
  // Button_Request_step3_SheetMetal_Submit = '버튼_견적요청_step3_판금가공_견적요청완료하기',
  // Button_Request_step3_MD_Submit = '버튼_견적요청_step3_기구설계_견적요청완료하기',
  // Button_SignUp_Step1_Next = '버튼_회원가입_step1_다음단계',
  // Button_SignUp_Step2_ClientType_CORPORATE = '버튼_회원가입_step2_고객유형_법인사업자',
  // Button_SignUp_Step2_ClientType_SELF = '버튼_회원가입_step2_고객유형_개인사업자',
  // Button_SignUp_Step2_ClientType_NON = '버튼_회원가입_step2_고객유형_사업자등록번호없는_개인및단체',
  // Button_SignUp_Step2_Address = '버튼_회원가입_step2_주소조회',
  // Button_SignUp_Step2_Prev = '버튼_회원가입_step2_이전단계',
  // Button_Dashboard_GNB_Request = '버튼_Dashboard_GNB_견적요청하기',
  // Button_Dashboard_GNB_Profile = '버튼_Dashboard_GNB_내정보관리',
  // Button_Dashboard_Tab_Request = '버튼_Dashboard_Tab_견적비교',
  // Button_Dashboard_Tab_Connect = '버튼_Dashboard_Tab_확정파트너',
  // Button_Request_Gnb_Login = '버튼_견적요청_GNB_고객 로그인/회원가입',
  // Button_Request_Tab_Request = '버튼_견적요청_견적비교',
  // Button_Request_Tab_Connect = '버튼_견적요청_확정파트너',
  // Button_Request_FileUpload = '버튼_견적요청_파일업로드',
  // Button_Request_Complete = '버튼_견적요청_견적요청완료하기',
  // Button_SignUp_Next = '버튼_회원가입_다음',
  // Button_SignUp_CompanyName = '버튼_회원가입_회사명',
  // Button_SignUp_ClientType_NON = '버튼_회원가입_고객유형_개인및단체',
  // Button_SignUp_ClientType_SELF = '버튼_회원가입_고객유형_개인사업자',
  // Button_SignUp_ClientType_CORPORATE = '버튼_회원가입_고객유형_법인사업자',
  // Button_SignUp_CompanyRegistrationNo = '버튼_회원가입_사업자등록번호',
  // Button_SignUp_Name = '버튼_회원가입_이름',
  // Button_SignUp_Phone = '버튼_회원가입_휴대폰번호',
  // Button_SignUp_Address = '버튼_회원가입_주소',
  // Button_SignUp_Website = '버튼_회원가입_웹사이트',
  // Button_SignUp_SelfDescription = '버튼_회원가입_소개글',
  // Button_SignUp_Prev = '버튼_회원가입_이전단계',

  /* 삭제 및 변경 대상 끝 */
  Button_Chat_Side_Viewer = '고객_채팅_도면뷰어',
  Button_Chat_Side_Quotation = '고객_채팅_견적서',
  Button_Chat_Side_PurchaseOrder = '고객_채팅_발주서',
  Button_Chat_Side_Request = '고객_채팅_요청서',
  Button_Chat_Side_DrawingFile_Management = '고객_채팅_도면관리',
  Button_Chat_Side_Help = '고객_채팅_도움말',

  Button_Open_Viewer = '고객_견적비교_뷰어로보기',
  Button_New_Request = '고객_견적비교_새견적요청하기',

  Landing_Reuqest = '고객_랜딩페이지220330_견적요청하기',
  Landing_About_Process = '고객_랜딩페이지220330_가공방식이궁금하다면?',

  Landing_CNC = '고객_랜딩페이지220330_CNC',
  Landing_CNC_MILLING = '고객_랜딩페이지220330_CNC_밀링',
  Landing_CNC_TURNING = '고객_랜딩페이지220330_CNC_선반',
  Landing_CNC_MC = '고객_랜딩페이지220330_CNC_머시닝',
  Landing_CNC_DRILLING = '고객_랜딩페이지220330_CNC_드릴링',
  Landing_CNC_RECOMMEND = '고객_랜딩페이지220330_CNC_추천',

  Landing_3DP = '고객_랜딩페이지220330_3DP',
  Landing_3DP_SLA = '고객_랜딩페이지220330_3DP_SLA',
  Landing_3DP_FDM = '고객_랜딩페이지220330_3DP_FDM',
  Landing_3DP_POLYJET = '고객_랜딩페이지220330_3DP_POLYJET',
  Landing_3DP_MJF = '고객_랜딩페이지220330_3DP_MJF',
  Landing_3DP_SLS = '고객_랜딩페이지220330_3DP_SLS',
  Landing_3DP_SLM = '고객_랜딩페이지220330_3DP_SLM',
  Landing_3DP_RECOMMEND = '고객_랜딩페이지220330_3DP_추천',

  Landing_INJ = '고객_랜딩페이지220330_INJ',
  Landing_INJ_IM = '고객_랜딩페이지220330_INJ_일반사출',
  Landing_INJ_TS = '고객_랜딩페이지220330_INJ_이중사출',
  Landing_INJ_BIM = '고객_랜딩페이지220330_INJ_블로우사출',
  Landing_INJ_IIM = '고객_랜딩페이지220330_INJ_인서트사출',
  Landing_INJ_RECOMMEND = '고객_랜딩페이지220330_추천',

  Landing_SM = '고객_랜딩페이지220330_SM',
  Landing_SM_PROCESS_METHOD_PRESS = '고객_랜딩페이지220330_SM_프레스',
  Landing_SM_PROCESS_METHOD_LC = '고객_랜딩페이지220330_SM_레이져커팅',
  Landing_SM_PROCESS_METHOD_WC = '고객_랜딩페이지220330_SM_워터젯',
  Landing_SM_PROCESS_METHOD_RECOMMEND = '고객_랜딩페이지220330_SM_추천',

  Landing_CASTING = '고객_랜딩페이지220330_CASTING',
  Landing_CASTING_PROCESS_METHOD_SAND = '고객_랜딩페이지220330_CASTING_사형주조',
  Landing_CASTING_PROCESS_METHOD_SHELL = '고객_랜딩페이지220330_CASTING_쉘주조',
  Landing_CASTING_PROCESS_METHOD_IC = '고객_랜딩페이지220330_CASTING_인베스트먼트주조',
  Landing_CASTING_PROCESS_METHOD_DC = '고객_랜딩페이지220330_CASTING_다이캐스팅',
  Landing_CASTING_PROCESS_METHOD_CC = '고객_랜딩페이지220330_CASTING_원심주조',
  Landing_CASTING_PROCESS_METHOD_RECOMMEND = '고객_랜딩페이지220330_CASTING_추천',

  Landing_PRODUCT_DESIGN = '고객_랜딩페이지220330_제품디자인',
  Landing_DESIGN = '고객_랜딩페이지220330_기구설계',

  Landing_PCB = '고객_랜딩페이지220330_PCB',
  Landing_PCB_SERVICE_DESIGN = '고객_랜딩페이지220330_PCB_설계',
  Landing_PCB_SERVICE_FABRICATION = '고객_랜딩페이지220330_PCB_제조',
  Landing_PCB_SERVICE_ASSEMBLY = '고객_랜딩페이지220330_PCB_조립',

  Landing_PROCESS_STEP1 = '고객_랜딩페이지220330_프로세스1',
  Landing_PROCESS_STEP2 = '고객_랜딩페이지220330_프로세스2',
  Landing_PROCESS_STEP3 = '고객_랜딩페이지220330_프로세스3',
  Landing_PROCESS_STEP4 = '고객_랜딩페이지220330_프로세스4',
  Landing_PROCESS_STEP_PREV = '고객_랜딩페이지220330_프로세스_이전',
  Landing_PROCESS_STEP_NEXT = '고객_랜딩페이지220330_프로세스_다음',

  Landing_MANUFACTURING_KNOWLEDGE_VIEW = '고객_랜딩페이지220330_제조지식더보기',
  Landing_MANUFACTURING_KNOWLEDGE_BLOG_CARD = '고객_랜딩페이지220330_블로그카드',
  Landing_MANUFACTURING_KNOWLEDGE_PARTNER_VIEW = '고객_랜딩페이지220330_파트너더보기',
  Landing_MANUFACTURING_KNOWLEDGE_PARTNER_CARD = '고객_랜딩페이지220330_파트너카드',

  Landing_PRESS_LIST = '고객_랜딩페이지220330_언론보도리스트',
  Landing_FAQ = '고객_랜딩페이지220330_자주묻는질문',

  Button_Capa_Viewer_Tutorial_Not_See_Again = '버튼_뷰어_튜토리얼_다시보지않기',
  Button_Capa_Viewer_Tutorial_See_Next_Time = '버튼_뷰어_튜토리얼_다음에보기',
  Button_Capa_Viewer_Tutorial_Again = '버튼_뷰어_튜토리얼_다시보기',
  Button_Capa_Viewer_Tutorial_Close_And_Chat = '버튼_뷰어_튜토리얼_닫고_채팅하기',
  Button_Capa_Viewer_Tutorial_Close = '버튼_뷰어_튜토리얼_닫기_X',
  Button_Capa_Viewer_Tutorial_BackDrop = '버튼_뷰어_튜토리얼_닫기_회색',
  Button_Capa_Viewer_Add_Annotation = '버튼_뷰어_주석달기',
  Button_Capa_Viewer_Open_Annotation = '버튼_뷰어_핀_클릭'
}

export enum Enum_Action_Link_Enum {
  Link_Main_Guide_CNC = '링크_메인_가이드보기_CNC',
  Link_Main_Guide_Printing = '링크_메인_가이드보기_3D프린팅',
  Link_Main_Guide_Molding = '링크_메인_가이드보기_금형사출',
  Link_Main_Guide_Sheet = '링크_메인_가이드보기_판금가공',
  Link_Main_Guide_MechanicalDesign = '링크_메인_가이드보기_기구설계',
  Link_Main_Guide_PD = '링크_메인_가이드보기_제품디자인',
  Link_Main_Join_Partner = '링크_메인_제조파트너가입하기',
  Link_Main_Gnb_Join_Partner = '링크_메인_GNB_CAPA제조파트너가입하기',
  Link_Request_Gnb_Join_Partner = '링크_견적요청_GNB_CAPA제조파트너가입하기'
}

export { CapaActivityType, Enum_Action_Button_Enum, Enum_Action_Page_Enum };

export default function useUserActionLog() {
  const { clientState } = useClient();
  const [writeUserActivityLog] = useWriteUserActivityLogMutation();
  const client = Amplitude.init('1e06602637046d8583c5c8cf8ba53873');
  const sessionStorageAccessToken = localStorage.getItem(
    STORAGE_ACTION.CLIENT_ACCESS_TOKEN
  );
  const { enqueueSnackbar } = useSnackbar();

  const token = clientState.accessToken || sessionStorageAccessToken;
  const getEmailFromToken: (token: string) => string | undefined = (token) => {
    const jwt = jwtDecode();
    return jwt?.aud as string;
  };

  const emailAddress = useMemo(() => {
    if (clientState.email) {
      return clientState.email;
    } else if (token) {
      return getEmailFromToken(token);
    } else {
      return undefined;
    }
  }, [clientState.email, token]);

  const getPublicIP = async () => {
    try {
      return publicIp.v4();
    } catch (e) {
      return false;
    }
  };

  const onInsertUserActionLogOne = useMemo(
    () =>
      debounce(
        async (
          action_id:
            | Enum_Action_Page_Enum
            | Enum_Action_Button_Enum
            | Enum_Action_Link_Enum,
          action_type: CapaActivityType,
          errorMessage?: any
        ) => {
          const isGhost = getStorageItem(STORAGE_ACTION.IS_GHOST_CLIENT);
          if (isGhost && isGhost === 'true') return;
          const ip = await getPublicIP();

          if (
            !AppConfig.baseUrl?.includes('staging') &&
            !AppConfig.baseUrl?.includes('localhost')
          ) {
            client.logEvent({
              event_type: `${action_type}/${action_id}`,
              user_id: emailAddress || ip || 'no ip',
              ip: ip || undefined,
              event_properties: {
                action_type,
                action_id
              },
              platform:
                navigator.platform === 'Win32' ? 'Windows' : navigator.platform,
              os_name: osName,
              os_version: osVersion,
              device_brand: mobileVendor,
              device_model: deviceType,
              user_properties: {
                browser: `${browserName} ${browserVersion}`,
                error: errorMessage || undefined
              }
            });
          }

          await writeUserActivityLog({
            variables: {
              input: {
                email: emailAddress,
                activityType: action_type,
                activitiId: action_id,
                ip: ip ? ip : undefined,
                os: `${osName} ${osVersion}`,
                deviceType: deviceType,
                browser: `${browserName} ${browserVersion}`,
                service: CapaServiceType.Quotation
              }
            }
          }).catch(() => {
            enqueueSnackbar(
              '서버와의 연결이 불안정합니다. 잠시 후 다시 시도해주세요',
              { variant: 'error' }
            );
          });
        },
        500
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientState.id]
  );

  const insertButtonLog = useCallback(
    (log: keyof typeof Enum_Action_Button_Enum) => {
      onInsertUserActionLogOne(
        Enum_Action_Button_Enum[log],
        CapaActivityType.ButtonClick
      );
    },
    [onInsertUserActionLogOne]
  );

  const insertPageLog = useCallback(
    (log: Enum_Action_Page_Enum) => {
      onInsertUserActionLogOne(log, CapaActivityType.PageVisit);
    },
    [onInsertUserActionLogOne]
  );

  return {
    onInsertUserActionLogOne,
    insertButtonLog,
    insertPageLog
  };
}
